import { dateStringSerializer } from "@megaron/date-string";
import { Serializers } from "@megaron/serializers";
import { UriBuilder, UriOfSchema, UriSchema, UriSpec } from "@megaron/uri";
import { uuidSerializer } from "@megaron/uuid";

import { budgetFiltersSerializer } from "./budgetFilters";
import { cartFiltersSerializer } from "./cartFilters";
import { blikCheckFiltersSerializer, cashRewardsAccountFiltersSerializer } from "./cashRewardsFilters";
import { customerAnalyticsViewFiltersSerializer } from "./customerAnalyticsFilters";
import { customerFiltersSerializer } from "./customerFilters";
import { fulfillmentOrderFiltersSerializer } from "./fulfillmentOrderFilters";
import { invoiceThreadFiltersSerializer } from "./invoiceThreadFilters";
import { itemFiltersSerializer } from "./itemFilters";
import { notificationFiltersSerializer } from "./notificationFilters";
import { orderFiltersSerializer } from "./orderFilters";
import { priceListsFiltersSerializer } from "./priceListFilters";
import { productAvailabilityFiltersSerializer } from "./productAvailabilityFilters";
import { regionProfitMetricsFiltersSerializer } from "./regionProfitMetricsFilters";
import { supportTicketFiltersSerializer } from "./supportTicketFilters";

export const visitViews = ["week", "day"] as const;
export type VisitView = (typeof visitViews)[number];

export const megaraxUriSchema = UriSchema({
  "megarax://home": UriSpec(),
  "megarax://history": UriSpec(),
  "megarax://signals": UriSpec(),

  "megarax://crm/customers": UriSpec({
    filters: customerFiltersSerializer.asJsonString().optional(),
  }),
  "megarax://crm/customers/new": UriSpec(),
  "megarax://crm/customers/new-list": UriSpec({
    filters: customerFiltersSerializer.asJsonString().optional(),
  }),
  "megarax://crm/customers/new-map": UriSpec({
    filters: customerFiltersSerializer.asJsonString().optional(),
  }),
  "megarax://crm/customers/new-customer-analytics": UriSpec({
    filters: customerAnalyticsViewFiltersSerializer.asJsonString().optional(),
  }),
  "megarax://crm/customer-lists/id/{id}": UriSpec({
    id: uuidSerializer,
    filters: customerFiltersSerializer.asJsonString().optional(),
  }),
  "megarax://crm/customers/id/{id}": UriSpec({ id: uuidSerializer }),

  "megarax://crm/orders": UriSpec({
    filters: orderFiltersSerializer.asJsonString().optional(),
  }),
  "megarax://crm/orders/number/{number}": UriSpec({ number: Serializers.string }),

  "megarax://crm/order-drafts": UriSpec(),
  "megarax://crm/order-drafts/new": UriSpec(),
  "megarax://crm/order-drafts/id/{id}": UriSpec({ id: uuidSerializer }),

  "megarax://crm/my-visits": UriSpec(),
  "megarax://crm/salespeople/id/{id}": UriSpec({ id: Serializers.string }),
  "megarax://crm/visits/id/{id}": UriSpec({ id: uuidSerializer }),
  "megarax://crm/salesperson-visits/id/{id}": UriSpec({ id: Serializers.string }),
  "megarax://crm/salesperson-visits/id/{id}/{view}": UriSpec({
    id: Serializers.string,
    view: Serializers.stringOneOf(...visitViews),
  }),
  "megarax://crm/salesperson-visits/id/{id}/{view}/{date}": UriSpec({
    id: Serializers.string,
    view: Serializers.stringOneOf(...visitViews),
    date: dateStringSerializer,
  }),
  "megarax://crm/salesperson-visits/id/{id}/{view}/{date}/visits/id/{visitId}": UriSpec({
    id: Serializers.string,
    view: Serializers.stringOneOf(...visitViews),
    date: dateStringSerializer,
    visitId: uuidSerializer,
  }),

  "megarax://crm/regions": UriSpec(),
  "megarax://crm/regions/id/{id}": UriSpec({
    id: Serializers.string,
    filters: regionProfitMetricsFiltersSerializer.asJsonString().optional(),
  }),

  "megarax://crm/trade-routes": UriSpec(),
  "megarax://crm/trade-routes/id/{id}": UriSpec({ id: uuidSerializer }),

  "megarax://crm/price-reports": UriSpec(),
  "megarax://crm/price-report-products": UriSpec(),
  "megarax://crm/diff-sync/{channel}": UriSpec({
    channel: Serializers.stringOneOf("sheetsPricingRule", "legacyMegaraxPricingRule"),
  }),

  "megarax://crm/home": UriSpec(),
  "megarax://crm/my-feed": UriSpec(),
  "megarax://crm/post-sale-discounts": UriSpec(),
  "megarax://crm/profit-targets": UriSpec(),
  "megarax://crm/my-affiliate-codes": UriSpec(),
  "megarax://crm/my-affiliate-codes/id/{id}": UriSpec({ id: Serializers.string }),
  "megarax://crm/product-availability": UriSpec({
    filters: productAvailabilityFiltersSerializer.asJsonString().optional(),
  }),
  "megarax://crm/training-calendar": UriSpec(),

  "megarax://catalog/items": UriSpec({
    filters: itemFiltersSerializer.asJsonString().optional(),
  }),
  "megarax://catalog/items/id/{id}": UriSpec({ id: Serializers.string }),
  "megarax://catalog/price-lists": UriSpec({
    filters: priceListsFiltersSerializer.asJsonString().optional(),
  }),
  "megarax://catalog/diff-sync/{channel}": UriSpec({
    channel: Serializers.stringOneOf(
      "bcItem",
      "sheetsItem",
      "sheetsPriceList",
      "sheetsPrice",
      "shopifyProduct",
      "shopifyVariant",
      "shopifyPrice",
      "legacyMegaraxPrice",
    ),
  }),

  "megarax://invoices/threads/id/{id}": UriSpec({ id: uuidSerializer }),
  "megarax://invoices/threads": UriSpec({
    filters: invoiceThreadFiltersSerializer.asJsonString().optional(),
  }),
  "megarax://invoices/budgets/id/{id}": UriSpec({ id: uuidSerializer }),
  "megarax://invoices/budgets": UriSpec({
    filters: budgetFiltersSerializer.asJsonString().optional(),
  }),
  "megarax://invoices/budgets/new": UriSpec(),
  "megarax://invoices/drafts/id/{id}": UriSpec({ id: uuidSerializer }),
  "megarax://invoices/drafts": UriSpec(),
  "megarax://invoices/drafts/new": UriSpec(),

  "megarax://iam/users/email/{email}": UriSpec({ email: Serializers.string }),
  "megarax://iam/users": UriSpec(),
  "megarax://iam/groups/id/{id}": UriSpec({ id: uuidSerializer }),
  "megarax://iam/groups": UriSpec(),
  "megarax://iam/groups/new": UriSpec(),

  "megarax://support/tickets/id/{id}": UriSpec({ id: uuidSerializer }),
  "megarax://support/tickets": UriSpec({
    filters: supportTicketFiltersSerializer.asJsonString().optional(),
  }),
  "megarax://support/tickets/new": UriSpec(),

  "megarax://loyalty/lines": UriSpec(),
  "megarax://loyalty/lines/id/{id}": UriSpec({ id: Serializers.string }),
  "megarax://loyalty/batches": UriSpec(),
  "megarax://loyalty/ungroup-code": UriSpec(),
  "megarax://loyalty/production-batches/id/{id}": UriSpec({ id: Serializers.string }),
  "megarax://loyalty/label-batches/id/{id}": UriSpec({ id: Serializers.string }),
  "megarax://loyalty/code-groups": UriSpec(),
  "megarax://loyalty/blik-checks": UriSpec({
    filters: blikCheckFiltersSerializer.asJsonString().optional(),
  }),
  "megarax://loyalty/accounts": UriSpec({
    filters: cashRewardsAccountFiltersSerializer.asJsonString().optional(),
  }),
  "megarax://loyalty/accounts/id/{id}": UriSpec({ id: Serializers.string }),
  "megarax://loyalty/inspect-code": UriSpec(),
  "megarax://loyalty/inspect-code/id/{id}": UriSpec({ id: Serializers.string }),
  "megarax://loyalty/carts": UriSpec({
    filters: cartFiltersSerializer.asJsonString().optional(),
  }),
  "megarax://loyalty/carts/id/{id}": UriSpec({ id: Serializers.string }),
  "megarax://loyalty/listings": UriSpec(),

  "megarax://support/settings": UriSpec(),

  "megarax://fulfillment/orders": UriSpec({
    filters: fulfillmentOrderFiltersSerializer.asJsonString().optional(),
  }),
  "megarax://fulfillment/orders/id/{id}": UriSpec({ id: Serializers.string }),

  "megarax://dev/environment": UriSpec(),
  "megarax://dev/notifications": UriSpec({
    filters: notificationFiltersSerializer.asJsonString().optional(),
  }),
  "megarax://dev/notifications/id/{id}": UriSpec({ id: uuidSerializer }),
  "megarax://dev/docs": UriSpec(),
});

export type MegaraxUriSchema = typeof megaraxUriSchema;
export const MegaraxUri = UriBuilder(megaraxUriSchema);
export type MegaraxUri = UriOfSchema<MegaraxUriSchema>;
